// Time Budget module for Docospot
// Handles time budget reports and analysis

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { API_BASE_URL } from '../core/config.js';
import { apiCall } from '../core/api.js';
import { showError, showSuccess } from '../core/ui.js';
import { getCurrentUser, formatDateTime, getEffectiveAccountId } from '../utils/helpers.js';
import { buildAccountHierarchyMap, getAllAccountIdsIncludingDescendants } from './accounts.js';
import { createCalendarPicker } from '../utils/calendar-picker.js';
import { timeTrackingState } from './time-tracking.js';

// Local modal functions
function closeModal() {
    const modal = document.getElementById('modal');
    if (modal) {
        modal.classList.add('hidden');
        modal.innerHTML = '';
    }
}

function showExportModal(entityName, columns, exportFunction) {
    const modal = document.getElementById('modal');
    if (!modal) return;
    
    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" id="export-modal-backdrop">
            <div class="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
                <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-medium text-gray-900">Export ${entityName} to Excel</h3>
                    <button onclick="closeModal()" class="text-gray-400 hover:text-gray-500">
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <div class="mb-4">
                    <p class="text-sm text-gray-600 mb-3">Select columns to export:</p>
                    <div class="space-y-2 max-h-96 overflow-y-auto">
                        ${columns.map(col => `
                            <label class="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input type="checkbox" 
                                       class="export-column-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" 
                                       value="${col.id}" 
                                       ${col.checked ? 'checked' : ''}>
                                <span class="text-sm text-gray-700">${col.label}</span>
                            </label>
                        `).join('')}
                    </div>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeModal()" 
                            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                        Cancel
                    </button>
                    <button onclick="window.currentExportFunction()" 
                            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
                        <svg class="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export
                    </button>
                </div>
            </div>
        </div>
    `;
    
    modal.classList.remove('hidden');
    window.currentExportFunction = exportFunction;
}

function exportToExcel(data, filename) {
    if (data.length === 0) {
        showError('No data to export');
        return;
    }
    
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert data array to worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Auto-size columns
    const colWidths = [];
    const headers = Object.keys(data[0]);
    
    headers.forEach((header, i) => {
        const maxLength = Math.max(
            header.length,
            ...data.map(row => String(row[header] || '').length)
        );
        colWidths[i] = { wch: Math.min(maxLength + 2, 50) }; // Cap at 50 characters
    });
    
    ws['!cols'] = colWidths;
    
    // Apply time formatting to time columns so Excel can sum them properly
    const timeColumns = ['Total Time (Hours)', 'Accumulated Hours'];
    
    timeColumns.forEach(timeColName => {
        const timeColumnIndex = headers.indexOf(timeColName);
        if (timeColumnIndex !== -1) {
            const colLetter = XLSX.utils.encode_col(timeColumnIndex);
            
            // Apply [h]:mm format to all cells in this column (allows hours > 24)
            for (let row = 1; row <= data.length; row++) {
                const cellRef = colLetter + (row + 1); // +1 because row 1 is header
                if (ws[cellRef] && typeof ws[cellRef].v === 'number' && ws[cellRef].v > 0) {
                    ws[cellRef].z = '[h]:mm'; // Format code for hours:minutes (allows > 24 hours)
                    ws[cellRef].t = 'n'; // Ensure it's treated as a number
                }
            }
        }
    });
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Export');
    
    // Write workbook and trigger download
    XLSX.writeFile(wb, filename);
}

// Permissions
const permissions = {
    can: (resource, action) => {
        const currentUser = getCurrentUser();
        if (!currentUser) return false;
        const role = currentUser.role;
        if (action === 'create') return ['Administrator', 'SuperAdmin', 'Team leader'].includes(role);
        if (action === 'edit' || action === 'delete') return ['Administrator', 'SuperAdmin'].includes(role);
        if (action === 'viewModifiedBadge' || action === 'viewManualBadge') return ['Administrator', 'SuperAdmin', 'Team leader'].includes(role);
        return false;
    }
};

// Cached time budget colouring settings (loaded once per session)
function parseTimeBudgetRedLimit(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : Math.max(0, parsed);
}

let timeBudgetColorSettings = {
    redLimitBelow: null, // minutes below budget at/beyond which the row is red (null = never red below)
    redLimitAbove: null, // minutes above budget at/beyond which the row is red (null = never red above)
    loaded: false
};

async function ensureTimeBudgetColorSettings(forceRefresh = false) {
    if (timeBudgetColorSettings.loaded && !forceRefresh) return timeBudgetColorSettings;
    try {
        const settings = await apiCall('/settings', {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        timeBudgetColorSettings.redLimitBelow = parseTimeBudgetRedLimit(settings.time_budget_red_limit_below);
        timeBudgetColorSettings.redLimitAbove = parseTimeBudgetRedLimit(settings.time_budget_red_limit_above);
    } catch (error) {
        console.error('[TIME BUDGET] Failed to load colouring settings:', error);
    }
    timeBudgetColorSettings.loaded = true;
    return timeBudgetColorSettings;
}

// ============================================================================
// TIME BUDGET FUNCTIONALITY (Extracted from original app.js)
// ============================================================================

async function showTimeBudgetTab() {
    // External contact does not have access to Time Budget
    if (window.permissions && !window.permissions.canAccessDataMenu('timeBudget')) {
        if (window.showTimeTrackingTab) window.showTimeTrackingTab();
        return;
    }

    const tabContent = document.getElementById('time-tab-content');
    if (!tabContent) return;

    // Update tab styling
    const timeTrackingTab = document.getElementById('time-tracking-tab');
    const timeBudgetTab = document.getElementById('time-budget-tab');
    
    if (timeTrackingTab && timeBudgetTab) {
        timeTrackingTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
        timeBudgetTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
    }

    const currentUser = getCurrentUser();

    tabContent.innerHTML = `
        <!-- Filter Row - Sticky -->
        <div class="sticky top-16 z-30 mb-0 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <!-- Row 1: Add Event Button (prominent) -->
                ${permissions.can('timeTracking', 'create') ? `
                <div class="flex justify-end mb-0.5">
                    <button onclick="openAddEventModal()" 
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        ${t('time.addEvent')}
                    </button>
                </div>
                ` : ''}
                
                <!-- Row 2: Period Type Selection and Period Selector -->
                <div class="flex items-end gap-4 mb-3 -mt-[15px]">
                    <!-- View By (Square buttons) -->
                    <div class="flex-shrink-0">
                        <label class="block text-sm font-medium text-gray-700 mb-2">${t('time.viewBy')}</label>
                        <div class="flex space-x-2">
                            <button type="button"
                                    data-budget-period-type="daily"
                                    onclick="toggleBudgetPeriodType('daily')"
                                    class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${budgetPeriodType === 'daily' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}">
                                ${t('time.daily')}
                            </button>
                            <button type="button"
                                    data-budget-period-type="weekly"
                                    onclick="toggleBudgetPeriodType('weekly')"
                                    class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${budgetPeriodType === 'weekly' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}">
                                ${t('time.weekly')}
                            </button>
                            <button type="button"
                                    data-budget-period-type="monthly"
                                    onclick="toggleBudgetPeriodType('monthly')"
                                    class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${budgetPeriodType === 'monthly' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}">
                                ${t('time.monthly')}
                            </button>
                        </div>
                    </div>
                    
                    <!-- Selected Periods Display -->
                    <div class="flex-1 relative">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.selectPeriods')}</label>
                        <div class="flex gap-2 items-center">
                            <div class="relative">
                                <button type="button"
                                        id="budget-period-range-button"
                                        class="inline-flex items-center justify-between min-w-[260px] h-[38px] rounded-md border border-gray-300 bg-white px-3 shadow-sm hover:bg-gray-50 text-sm">
                                    <span id="budget-period-range-display" class="text-gray-700">${formatBudgetPeriodLabel()}</span>
                                    <svg class="ml-2 w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z"/>
                                    </svg>
                                </button>
                                <input type="hidden" id="budget-period-date-from" value="${formatBudgetDateInputValue(selectedBudgetRangeStart)}">
                                <input type="hidden" id="budget-period-date-to" value="${formatBudgetDateInputValue(selectedBudgetRangeEnd)}">
                                <div id="budget-calendar-popup" class="hidden fixed z-50 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 p-4" style="width: 320px;"></div>
                            </div>
                            <label class="inline-flex items-center h-[38px] px-3 text-sm text-gray-700 cursor-pointer select-none">
                                <input type="checkbox"
                                       id="budget-hide-empty-periods"
                                       onchange="toggleBudgetHideEmptyPeriods(this.checked)"
                                       ${budgetHideEmptyPeriods ? 'checked' : ''}
                                       class="h-4 w-4 mr-2 text-primary-600 border-gray-300 rounded focus:ring-primary-500">
                                ${t('time.onlyShowPeriodsWithActivity')}
                            </label>
                        </div>
                    </div>
                    
                    <!-- Action buttons -->
                    <div class="flex items-end space-x-2">
                        <button onclick="clearTimeBudgetFilters()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            ${t('common.clear')}
                        </button>
                        <button onclick="loadTimeBudgetData(true)" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            ${t('common.refresh')}
                        </button>
                        <button onclick="exportTimeBudget()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            ${t('common.export')}
                        </button>
                    </div>
                </div>

                <!-- Row 2: Account and Location Filters -->
                <div class="flex items-end gap-4">
                    <!-- Account Filter -->
                    <div class="flex-1">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.account')}</label>
                        <div class="relative">
                            <button type="button" id="budget-account-filter-toggle" 
                                    onclick="toggleBudgetAccountFilterExpanded()"
                                    class="w-full text-left border border-gray-300 rounded-md px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                <div class="flex items-center justify-between">
                                    <span id="budget-account-filter-display" class="text-sm text-gray-500 italic">Select accounts to filter locations...</span>
                                    <svg id="budget-account-filter-arrow" class="w-4 h-4 text-gray-400 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </div>
                            </button>
                            <div id="budget-account-filter-tree-container" class="hidden absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                <div id="budget-account-filter-tree" class="p-2 max-h-64 overflow-y-auto">
                                    <div class="text-gray-400 text-sm">Loading accounts...</div>
                                </div>
                                <div class="border-t border-gray-200 px-3 py-2 bg-gray-50 flex items-center justify-end">
                                    <button type="button" onclick="clearBudgetAccountFilter()" class="text-sm text-gray-600 hover:text-gray-700">
                                        ${t('common.clear')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Location Filter -->
                    <div class="flex-1">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.location')}</label>
                        <div class="relative">
                            <button type="button" id="budget-location-filter-toggle" 
                                    onclick="toggleBudgetLocationFilterExpanded()"
                                    class="w-full text-left border border-gray-300 rounded-md px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                <div class="flex items-center justify-between">
                                    <span id="budget-location-filter-display" class="text-sm text-gray-500 italic">Select locations to include...</span>
                                    <svg id="budget-location-filter-arrow" class="w-4 h-4 text-gray-400 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </div>
                            </button>
                            <div id="budget-location-filter-tree-container" class="hidden absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                <!-- Header with select all checkbox -->
                                <div class="border-b border-gray-200 px-3 py-2 bg-gray-50">
                                    <label class="flex items-center cursor-pointer hover:bg-gray-100 -mx-1 px-1 py-1 rounded">
                                        <input type="checkbox" 
                                               id="budget-location-select-all-checkbox"
                                               onchange="toggleAllBudgetLocations(this.checked)"
                                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                        <span id="budget-location-count-label" class="ml-2 text-sm text-gray-700 font-medium">0 locations</span>
                                    </label>
                                </div>
                                <div class="p-2 border-b border-gray-200">
                                    <input type="text" id="budget-location-search"
                                           placeholder="${t('time.searchLocations')}"
                                           autocomplete="off"
                                           class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                </div>
                                <div id="budget-location-filter-list" class="p-2 max-h-64 overflow-y-auto" onscroll="handleBudgetLocationScroll(event)">
                                    <div class="text-gray-400 text-sm">Loading locations...</div>
                                </div>
                                <div class="border-t border-gray-200 px-3 py-2 bg-gray-50 flex items-center justify-end">
                                    <button type="button" onclick="clearBudgetLocationFilter()" class="text-sm text-gray-600 hover:text-gray-700">
                                        ${t('common.clear')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Row 3: Single-location stats (populated after data loads) -->
                <div id="budget-topbar-stats" class="hidden mt-3 pt-3 border-t border-gray-300"></div>
            </div>
        </div>

        <!-- Content Area -->
        <div class="bg-white shadow rounded-lg overflow-hidden mt-1">
            <div id="time-budget-content" class="text-center py-12 text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900">Time Budget Report</h3>
                <p class="mt-1 text-sm text-gray-500">Follow these steps to generate your report:</p>
                <ol class="mt-4 text-sm text-gray-600 text-left max-w-md mx-auto space-y-2">
                    <li class="flex items-start">
                        <span class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-xs mr-3">1</span>
                        <span>Select a <strong>date range</strong> to generate time periods (daily, weekly or monthly)</span>
                    </li>
                    <li class="flex items-start">
                        <span class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-xs mr-3">2</span>
                        <span>Choose <strong>accounts</strong> to filter locations (optional, or select specific locations below)</span>
                    </li>
                    <li class="flex items-start">
                        <span class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-xs mr-3">3</span>
                        <span>Select one or more <strong>locations</strong> to include in the report</span>
                    </li>
                    <li class="flex items-start">
                        <span class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-xs mr-3">4</span>
                        <span>Click <strong>Refresh</strong> to load the time budget data</span>
                    </li>
                </ol>
            </div>
        </div>
    `;

    // Default view-by mode is daily on first load
    if (!budgetPeriodType) budgetPeriodType = 'daily';

    // Load accounts and locations (but don't select all or load data by default)
    await loadAccountsForTimeBudget();
    await loadBudgetLocations(true);
    setupBudgetLocationSearch();
    // Don't call loadTimeBudgetData() - let user select filters first

    // Recompute selected period for current date + type
    updateBudgetPeriodRange();

    // Recreate range date picker (DOM was rebuilt so old instance is stale)
    setTimeout(() => {
        window.budgetPeriodCalendar = null;
        window.budgetPeriodCalendar = createCalendarPicker({
            inputId: 'budget-period-date-from',
            buttonId: 'budget-period-range-button',
            displayId: 'budget-period-range-display',
            popupId: 'budget-calendar-popup',
            isRange: true,
            defaultDate: selectedBudgetRangeStart,
            onChange: (result) => {
                if (result && result.startDate && result.endDate) {
                    selectedBudgetRangeStart = new Date(result.startDate);
                    selectedBudgetRangeStart.setHours(0, 0, 0, 0);
                    selectedBudgetRangeEnd = new Date(result.endDate);
                    selectedBudgetRangeEnd.setHours(0, 0, 0, 0);
                    updateBudgetPeriodRange();
                }
            }
        });
        // Register in global registry so the inline onclick handlers in the
        // calendar popup HTML (window.calendarPickers[popupId].selectDate, etc.)
        // can find this picker instance.
        if (!window.calendarPickers) window.calendarPickers = {};
        window.calendarPickers['budget-calendar-popup'] = window.budgetPeriodCalendar;
    }, 100);
}

// Time Budget state: a date range + view-by mode determines the periods
// being analysed. selectedBudgetPeriods is recomputed from the range and is
// always an array — possibly multiple entries — so downstream data fetch,
// display and export logic that iterates periods continues to work.
let selectedBudgetRangeStart = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
let selectedBudgetRangeEnd = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
let selectedBudgetPeriods = [];
let budgetPeriodType = 'daily';
// Persisted user preference: hide periods (rows) with zero events.
let budgetHideEmptyPeriods = (() => {
    try { return localStorage.getItem('budgetHideEmptyPeriods') === 'true'; }
    catch { return false; }
})();

function toggleBudgetHideEmptyPeriods(checked) {
    budgetHideEmptyPeriods = !!checked;
    try { localStorage.setItem('budgetHideEmptyPeriods', budgetHideEmptyPeriods ? 'true' : 'false'); }
    catch { /* ignore quota / privacy errors */ }
    // Re-render with cached data if we have any
    if (Object.keys(budgetLocationsState.locationsData || {}).length > 0) {
        loadTimeBudgetData(false);
    }
}

// Toggle the date picker
function toggleBudgetPeriodRangePicker() {
    if (window.budgetPeriodCalendar && window.budgetPeriodCalendar.toggle) {
        window.budgetPeriodCalendar.toggle();
    }
}

function toggleBudgetPeriodType(type) {
    budgetPeriodType = type;
    // Restyle the period buttons to reflect the new selection
    const activeClasses = ['bg-primary-600', 'text-white'];
    const inactiveClasses = ['bg-white', 'text-gray-700', 'hover:bg-gray-100', 'border', 'border-gray-200'];
    document.querySelectorAll('[data-budget-period-type]').forEach(btn => {
        const isActive = btn.dataset.budgetPeriodType === type;
        const toAdd = isActive ? activeClasses : inactiveClasses;
        const toRemove = isActive ? inactiveClasses : activeClasses;
        btn.classList.remove(...toRemove);
        btn.classList.add(...toAdd);
    });
    updateBudgetPeriodRange();
}

// Reset selected range to today
function goToBudgetToday() {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    selectedBudgetRangeStart = t;
    selectedBudgetRangeEnd = new Date(t);
    syncBudgetPickerInputs();
    if (window.budgetPeriodCalendar && typeof window.budgetPeriodCalendar.setRange === 'function') {
        window.budgetPeriodCalendar.setRange(selectedBudgetRangeStart, selectedBudgetRangeEnd);
    }
    updateBudgetPeriodRange();
}

function syncBudgetPickerInputs() {
    const fromInput = document.getElementById('budget-period-date-from');
    const toInput = document.getElementById('budget-period-date-to');
    if (fromInput) fromInput.value = formatBudgetDateInputValue(selectedBudgetRangeStart);
    if (toInput) toInput.value = formatBudgetDateInputValue(selectedBudgetRangeEnd);
}

function formatBudgetDateInputValue(d) {
    const dt = d || new Date();
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

// Human-readable label for the selected range. When the range collapses
// onto a single period (e.g. a single day in daily mode, a single week in
// weekly mode) we show that period's label; otherwise we show the start
// and end dates with the number of resulting periods.
function formatBudgetPeriodLabel() {
    const start = selectedBudgetRangeStart || new Date();
    const end = selectedBudgetRangeEnd || start;
    const sameDay = start.getFullYear() === end.getFullYear()
        && start.getMonth() === end.getMonth()
        && start.getDate() === end.getDate();

    if (budgetPeriodType === 'daily') {
        if (sameDay) {
            return start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        return `${formatDate(start)} - ${formatDate(end)} ${end.getFullYear()}`;
    }
    if (budgetPeriodType === 'weekly') {
        const startWeek = getWeekBounds(start);
        const endWeek = getWeekBounds(end);
        const sameWeek = startWeek.start.getTime() === endWeek.start.getTime();
        if (sameWeek) {
            const weekNum = getWeekNumber(startWeek.start);
            return `Week ${weekNum}, ${startWeek.start.getFullYear()} (${formatDate(startWeek.start)} - ${formatDate(startWeek.end)})`;
        }
        return `${formatDate(startWeek.start)} - ${formatDate(endWeek.end)} ${endWeek.end.getFullYear()}`;
    }
    // monthly
    const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
    if (sameMonth) {
        return start.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }
    const startLabel = start.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    const endLabel = end.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    return `${startLabel} - ${endLabel}`;
}

// Get Monday/Sunday bounds (00:00) for the ISO week containing `date`.
function getWeekBounds(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const daysFromMonday = day === 0 ? 6 : day - 1;
    const start = new Date(d);
    start.setDate(start.getDate() - daysFromMonday);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
}

// Recompute the array of periods covered by [selectedBudgetRangeStart,
// selectedBudgetRangeEnd] given the current view-by mode. Each period the
// range overlaps is emitted once so downstream fetch / display / export
// logic can iterate it.
function updateBudgetPeriodRange() {
    const start = selectedBudgetRangeStart || new Date();
    const end = selectedBudgetRangeEnd || start;
    selectedBudgetPeriods = [];

    if (budgetPeriodType === 'daily') {
        const cursor = new Date(start);
        cursor.setHours(0, 0, 0, 0);
        const last = new Date(end);
        last.setHours(0, 0, 0, 0);
        while (cursor.getTime() <= last.getTime()) {
            const year = cursor.getFullYear();
            const month = String(cursor.getMonth() + 1).padStart(2, '0');
            const day = String(cursor.getDate()).padStart(2, '0');
            const periodKey = `${year}-${month}-${day}`;
            const periodLabel = cursor.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            selectedBudgetPeriods.push({ key: periodKey, label: periodLabel });
            cursor.setDate(cursor.getDate() + 1);
        }
    } else if (budgetPeriodType === 'weekly') {
        const firstWeek = getWeekBounds(start);
        const lastWeek = getWeekBounds(end);
        const cursor = new Date(firstWeek.start);
        while (cursor.getTime() <= lastWeek.start.getTime()) {
            const weekEnd = new Date(cursor);
            weekEnd.setDate(weekEnd.getDate() + 6);
            const weekNum = getWeekNumber(cursor);
            const year = cursor.getFullYear();
            const periodKey = `${year}-W${String(weekNum).padStart(2, '0')}`;
            const periodLabel = `Week ${weekNum}, ${year} (${formatDate(cursor)} - ${formatDate(weekEnd)})`;
            selectedBudgetPeriods.push({ key: periodKey, label: periodLabel });
            cursor.setDate(cursor.getDate() + 7);
        }
    } else {
        // monthly
        const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
        const last = new Date(end.getFullYear(), end.getMonth(), 1);
        while (cursor.getTime() <= last.getTime()) {
            const year = cursor.getFullYear();
            const month = cursor.getMonth() + 1;
            const periodKey = `${year}-${String(month).padStart(2, '0')}`;
            const periodLabel = cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
            selectedBudgetPeriods.push({ key: periodKey, label: periodLabel });
            cursor.setMonth(cursor.getMonth() + 1);
        }
    }

    updateSelectedPeriodsDisplay();
    filterTimeBudget();
}

function addBudgetPeriod() {
    // No longer used but kept for compatibility
    updateBudgetPeriodRange();
}

function updateSelectedPeriodsDisplay() {
    // Update the date display label on the picker button so it reflects
    // the current selected date + view-by mode.
    const display = document.getElementById('budget-period-range-display');
    if (display) display.textContent = formatBudgetPeriodLabel();
}

function formatDate(date) {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function generateWeekOptions() {
    const weeks = [];
    const currentDate = new Date();
    
    // Generate last 12 weeks and next 4 weeks
    for (let i = 12; i >= -4; i--) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - (i * 7));
        
        // Get week number
        const weekNum = getWeekNumber(date);
        const year = date.getFullYear();
        
        // Get start and end of week
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay() + 1); // Monday
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
        
        const label = `Week ${weekNum}, ${year} (${startOfWeek.toLocaleDateString('en-GB', {day: '2-digit', month: 'short'})} - ${endOfWeek.toLocaleDateString('en-GB', {day: '2-digit', month: 'short'})})`;
        const value = `${year}-W${String(weekNum).padStart(2, '0')}`;
        
        weeks.push({ label, value });
    }
    
    return weeks.reverse(); // Most recent first
}

function generateMonthOptions() {
    const months = [];
    const currentDate = new Date();
    
    // Generate last 12 months and next 3 months
    for (let i = 12; i >= -3; i--) {
        const date = new Date(currentDate);
        date.setMonth(date.getMonth() - i);
        
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const monthName = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        
        const label = monthName;
        const value = `${year}-${String(month).padStart(2, '0')}`;
        
        months.push({ label, value });
    }
    
    return months.reverse(); // Most recent first
}

function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
    return Math.ceil((((d - yearStart) / 86400000) + 1)/7);
}

function toggleBudgetAccountFilterExpanded() {
    const container = document.getElementById('budget-account-filter-tree-container');
    const arrow = document.getElementById('budget-account-filter-arrow');
    
    if (container && arrow) {
        const wasHidden = container.classList.contains('hidden');
        
        // Close location filter if open
        closeBudgetLocationFilter();
        
        container.classList.toggle('hidden');
        arrow.classList.toggle('rotate-180');
        
        // If we just opened it, set up click-outside listener
        if (wasHidden) {
            setTimeout(() => {
                document.addEventListener('click', closeBudgetFiltersOnClickOutside, true);
            }, 0);
        }
    }
}

function toggleBudgetLocationFilterExpanded() {
    const container = document.getElementById('budget-location-filter-tree-container');
    const arrow = document.getElementById('budget-location-filter-arrow');
    
    if (container && arrow) {
        const wasHidden = container.classList.contains('hidden');
        
        // Close account filter if open
        closeBudgetAccountFilter();
        
        container.classList.toggle('hidden');
        arrow.classList.toggle('rotate-180');
        
        // If we just opened it, set up click-outside listener
        if (wasHidden) {
            setTimeout(() => {
                document.addEventListener('click', closeBudgetFiltersOnClickOutside, true);
            }, 0);
        }
    }
}

function closeBudgetAccountFilter() {
    const container = document.getElementById('budget-account-filter-tree-container');
    const arrow = document.getElementById('budget-account-filter-arrow');
    
    if (container && !container.classList.contains('hidden')) {
        container.classList.add('hidden');
        if (arrow) arrow.classList.remove('rotate-180');
    }
}

function closeBudgetLocationFilter() {
    const container = document.getElementById('budget-location-filter-tree-container');
    const arrow = document.getElementById('budget-location-filter-arrow');
    
    if (container && !container.classList.contains('hidden')) {
        container.classList.add('hidden');
        if (arrow) arrow.classList.remove('rotate-180');
    }
}

function closeBudgetFiltersOnClickOutside(event) {
    const accountToggle = document.getElementById('budget-account-filter-toggle');
    const accountContainer = document.getElementById('budget-account-filter-tree-container');
    const locationToggle = document.getElementById('budget-location-filter-toggle');
    const locationContainer = document.getElementById('budget-location-filter-tree-container');
    
    // Check if click is outside account filter
    if (accountToggle && accountContainer && 
        !accountToggle.contains(event.target) && 
        !accountContainer.contains(event.target)) {
        closeBudgetAccountFilter();
    }
    
    // Check if click is outside location filter
    if (locationToggle && locationContainer && 
        !locationToggle.contains(event.target) && 
        !locationContainer.contains(event.target)) {
        closeBudgetLocationFilter();
    }
    
    // Remove listener if both are closed
    if ((accountContainer && accountContainer.classList.contains('hidden')) &&
        (locationContainer && locationContainer.classList.contains('hidden'))) {
        document.removeEventListener('click', closeBudgetFiltersOnClickOutside, true);
    }
}

function clearBudgetAccountFilter() {
    console.log('[CLEAR ACCOUNTS] Clearing account filter and locations');
    
    // Clear selected accounts
    selectedBudgetAccounts.clear();
    
    // Uncheck all checkboxes
    const checkboxes = document.querySelectorAll('#budget-account-filter-tree input[type="checkbox"]');
    checkboxes.forEach(cb => cb.checked = false);
    
    // Update display
    const display = document.getElementById('budget-account-filter-display');
    if (display) {
        display.textContent = t('time.allAccounts');
    }
    
    // Clear locations since they depend on account selection
    console.log('[CLEAR ACCOUNTS] Locations before clear:', Array.from(selectedBudgetLocations));
    selectedBudgetLocations.clear();
    console.log('[CLEAR ACCOUNTS] Locations after clear:', Array.from(selectedBudgetLocations));
    
    budgetLocationsState.allLocations = [];
    displayBudgetLocations();
    updateBudgetLocationFilterDisplay();
    updateBudgetLocationCheckboxState();
    
    filterTimeBudget();
}

function selectAllBudgetAccounts() {
    // Select all accounts from the hierarchy
    const selectAllRecursive = (accounts) => {
        accounts.forEach(account => {
            selectedBudgetAccounts.add(account.id);
            if (account.children && account.children.length > 0) {
                selectAllRecursive(account.children);
            }
        });
    };
    
    // Get all root accounts from the hierarchy map
    const rootAccounts = Object.values(budgetAccountHierarchyMap).filter(acc => !acc.parent_id);
    selectAllRecursive(rootAccounts);
    
    // Check all checkboxes
    const checkboxes = document.querySelectorAll('#budget-account-filter-tree input[type="checkbox"]');
    checkboxes.forEach(cb => cb.checked = true);
    
    // Update display
    updateBudgetAccountFilterDisplay();
    filterTimeBudget();
}

function deselectAllBudgetAccounts() {
    // Clear all selected accounts
    selectedBudgetAccounts.clear();
    
    // Uncheck all checkboxes
    const checkboxes = document.querySelectorAll('#budget-account-filter-tree input[type="checkbox"]');
    checkboxes.forEach(cb => cb.checked = false);
    
    // Update display
    updateBudgetAccountFilterDisplay();
    filterTimeBudget();
}


function clearBudgetLocationFilter() {
    // Clear selected locations
    selectedBudgetLocations.clear();
    
    // Uncheck all checkboxes and update display
    displayBudgetLocations();
    
    // Update display
    const display = document.getElementById('budget-location-filter-display');
    if (display) {
        display.textContent = t('time.allLocations');
    }
    
    filterTimeBudget();
}

function selectAllBudgetLocations() {
    // Select all currently loaded locations
    budgetLocationsState.allLocations.forEach(location => {
        const locationId = location._id || location.id;
        selectedBudgetLocations.add(locationId);
    });
    
    // Update display
    displayBudgetLocations();
    updateBudgetLocationFilterDisplay();
    updateBudgetLocationCheckboxState();
    filterTimeBudget();
}

function deselectAllBudgetLocations() {
    console.log('[DESELECT ALL] Called - clearing selectedBudgetLocations');
    console.log('[DESELECT ALL] Before:', Array.from(selectedBudgetLocations));
    
    // Deselect all locations
    selectedBudgetLocations.clear();
    
    console.log('[DESELECT ALL] After:', Array.from(selectedBudgetLocations));
    
    // Update display
    displayBudgetLocations();
    updateBudgetLocationFilterDisplay();
    updateBudgetLocationCheckboxState();
    filterTimeBudget();
}

function toggleAllBudgetLocations(checked) {
    if (checked) {
        selectAllBudgetLocations();
    } else {
        deselectAllBudgetLocations();
    }
}

function updateBudgetLocationCheckboxState() {
    const selectAllCheckbox = document.getElementById('budget-location-select-all-checkbox');
    const countLabel = document.getElementById('budget-location-count-label');
    
    if (!selectAllCheckbox || !countLabel) return;
    
    const totalLocations = budgetLocationsState.allLocations.length;
    
    // Update label
    countLabel.textContent = `${totalLocations} ${totalLocations === 1 ? 'location' : 'locations'}`;
    
    // Update checkbox state (checked if all are selected, indeterminate if some are selected)
    if (selectedBudgetLocations.size === 0) {
        selectAllCheckbox.checked = false;
        selectAllCheckbox.indeterminate = false;
    } else if (selectedBudgetLocations.size === totalLocations) {
        selectAllCheckbox.checked = true;
        selectAllCheckbox.indeterminate = false;
    } else {
        selectAllCheckbox.checked = false;
        selectAllCheckbox.indeterminate = true;
    }
}


function filterTimeBudget() {
    const contentArea = document.getElementById('time-budget-content');
    
    console.log('[FILTER DEBUG] Periods:', selectedBudgetPeriods.length);
    console.log('[FILTER DEBUG] Accounts:', selectedBudgetAccounts.size);
    console.log('[FILTER DEBUG] Locations:', selectedBudgetLocations.size);
    
    // Only load data if we have periods and at least one account or location is selected
    if (selectedBudgetPeriods.length > 0 && 
        (selectedBudgetAccounts.size > 0 || selectedBudgetLocations.size > 0)) {
        console.log('[FILTER DEBUG] Loading data...');
        loadTimeBudgetData(true);
    } else {
        console.log('[FILTER DEBUG] Showing placeholder...');
        // Clear existing data and show the placeholder if filters aren't complete
        if (contentArea) {
            contentArea.innerHTML = `
                <div class="text-center py-12 text-gray-500">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">${t('time.timeBudget')}</h3>
                    <p class="mt-1 text-sm text-gray-500">Select at least one period and one account or location to view time budget data...</p>
                </div>
            `;
        }
    }
}

function clearTimeBudgetFilters() {
    // Reset range to today and recompute periods for the current view-by mode.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedBudgetRangeStart = today;
    selectedBudgetRangeEnd = new Date(today);
    syncBudgetPickerInputs();
    if (window.budgetPeriodCalendar && typeof window.budgetPeriodCalendar.setRange === 'function') {
        window.budgetPeriodCalendar.setRange(selectedBudgetRangeStart, selectedBudgetRangeEnd);
    }

    // Clear account and location filters
    clearBudgetAccountFilter();
    clearBudgetLocationFilter();

    console.log('Clearing time budget filters...');
    updateBudgetPeriodRange();
}

// Time Budget Account Filter State
let selectedBudgetAccounts = new Set();
let budgetAccountHierarchyMap = {};

async function loadAccountsForTimeBudget() {
    try {
        const accountId = getEffectiveAccountId();
        if (!accountId) {
            console.log('[TIME BUDGET] No account selected, skipping account load');
            return;
        }
        
        console.log('[TIME BUDGET] Fetching accounts from /api/accounts/hierarchy');
        const response = await fetch(`${API_BASE_URL}/accounts/hierarchy?account_id=${accountId}`, {
            headers: {
                'Authorization': `Bearer ${auth.token}`
            }
        });
        
        console.log('[TIME BUDGET] Response status:', response.status);
        
        if (response.ok) {
            const data = await response.json();
            console.log('[TIME BUDGET] Received hierarchy response:', data);
            
            // Extract accounts_tree from response
            const accountsTree = data.accounts_tree || data;
            
            // Build hierarchy map for filtering
            budgetAccountHierarchyMap = buildAccountHierarchyMap(accountsTree);
            console.log('[TIME BUDGET] Account hierarchy map:', budgetAccountHierarchyMap);
            
            // Render tree with checkboxes
            const treeContainer = document.getElementById('budget-account-filter-tree');
            if (treeContainer) {
                treeContainer.innerHTML = renderBudgetAccountTree(accountsTree);
            }
        }
    } catch (error) {
        console.error('[TIME BUDGET] Error loading accounts:', error);
        const treeContainer = document.getElementById('budget-account-filter-tree');
        if (treeContainer) {
            treeContainer.innerHTML = '<div class="text-red-600 text-sm p-2">Error loading accounts</div>';
        }
    }
}

function renderBudgetAccountTree(accounts, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        // Auto-expand top level so users see sub-accounts without an extra click,
        // matching the behaviour of the Time Tracking list view.
        const expandByDefault = level === 0;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleBudgetAccountItem('budget-tree-${accountId}')" 
                                class="mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                            <svg id="budget-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform${expandByDefault ? ' rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="w-5 mr-1"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="checkbox" value="${accountId}" 
                               onchange="selectBudgetAccount('${accountId}', '${accountName.replace(/'/g, "\\'")}')"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="budget-tree-${accountId}"${expandByDefault ? '' : ' class="hidden"'}>
                        ${renderBudgetAccountTree(account.children, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

function toggleBudgetAccountItem(itemId) {
    const item = document.getElementById(itemId);
    const iconId = itemId.replace('budget-tree-', 'budget-tree-icon-');
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

function selectBudgetAccount(accountId, accountName) {
    const checkbox = event.target;
    
    if (checkbox.checked) {
        selectedBudgetAccounts.add(accountId);
    } else {
        selectedBudgetAccounts.delete(accountId);
    }
    
    updateBudgetAccountFilterDisplay();
    
    // Reload locations when account selection changes
    loadBudgetLocations(true);
    
    filterTimeBudget();
}

function updateBudgetAccountFilterDisplay() {
    const display = document.getElementById('budget-account-filter-display');
    if (!display) return;
    
    if (selectedBudgetAccounts.size === 0) {
        display.textContent = t('time.allAccounts');
    } else if (selectedBudgetAccounts.size === 1) {
        // Find the account name from the checkbox
        const accountId = Array.from(selectedBudgetAccounts)[0];
        const checkbox = document.querySelector(`#budget-account-filter-tree input[value="${accountId}"]`);
        const label = checkbox ? checkbox.closest('label').querySelector('span').textContent : '';
        display.textContent = label || `1 ${t('common.account')}`;
    } else {
        display.textContent = `${selectedBudgetAccounts.size} ${t('common.accounts')}`;
    }
}

// Time Budget Location Filter State
let selectedBudgetLocations = new Set();
let budgetLocationsState = {
    allLocations: [],
    currentSkip: 0,
    limit: 100,
    hasMore: true,
    isLoading: false,
    searchTerm: '',
    allEvents: [], // Store all events for export
    locationsData: {} // Store location budget data
};

function setupBudgetLocationSearch() {
    const searchInput = document.getElementById('budget-location-search');
    const locationList = document.getElementById('budget-location-filter-list');
    if (!searchInput || searchInput.dataset.bound === 'true') return;
    searchInput.dataset.bound = 'true';

    let searchTimeout;
    searchInput.addEventListener('input', function () {
        const searchTerm = this.value.trim();
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
            budgetLocationsState.searchTerm = searchTerm;
            loadBudgetLocations(true);
        }, 300);
    });

    if (locationList && locationList.dataset.scrollBound !== 'true') {
        locationList.dataset.scrollBound = 'true';
        // Existing inline onscroll already handles infinite scroll; nothing to add
    }
}

async function loadBudgetLocations(reset = false) {
    if (reset) {
        budgetLocationsState.currentSkip = 0;
        budgetLocationsState.allLocations = [];
        budgetLocationsState.hasMore = true;
    }
    
    // If no accounts are selected, clear locations and don't load
    if (selectedBudgetAccounts.size === 0) {
        selectedBudgetLocations.clear();
        budgetLocationsState.allLocations = [];
        displayBudgetLocations();
        updateBudgetLocationFilterDisplay();
        updateBudgetLocationCheckboxState();
        return;
    }
    
    if (budgetLocationsState.isLoading || !budgetLocationsState.hasMore) {
        return;
    }
    
    budgetLocationsState.isLoading = true;
    
    try {
        // Build query parameters including account filter
        const params = new URLSearchParams();
        params.append('skip', budgetLocationsState.currentSkip);
        params.append('limit', budgetLocationsState.limit);
        // Don't use minimal=true so we get budget fields
        
        if (budgetLocationsState.searchTerm) {
            params.append('search', budgetLocationsState.searchTerm);
        }
        
        // Add account filter - we know selectedBudgetAccounts.size > 0 at this point
        const accountIds = [];
        selectedBudgetAccounts.forEach(accountId => {
            const idsWithDescendants = getAllAccountIdsIncludingDescendants(accountId, budgetAccountHierarchyMap);
            accountIds.push(...idsWithDescendants);
        });
        // Remove duplicates
        const uniqueAccountIds = [...new Set(accountIds)];
        uniqueAccountIds.forEach(id => params.append('account_id', id));
        
        console.log('[TIME BUDGET] Loading locations with params:', params.toString());
        
        const data = await apiCall(`/locations?${params.toString()}`, {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const locations = Array.isArray(data) ? data : data.locations || [];
        console.log('[TIME BUDGET] Loaded locations:', locations.length);
        
        budgetLocationsState.allLocations = [...budgetLocationsState.allLocations, ...locations]
            .sort((a, b) => (a?.name || '').localeCompare((b?.name || ''), undefined, { sensitivity: 'base' }));
        budgetLocationsState.hasMore = locations.length === budgetLocationsState.limit;
        budgetLocationsState.currentSkip += locations.length;
        
        displayBudgetLocations();
        updateBudgetLocationFilterDisplay();
        updateBudgetLocationCheckboxState();
    } catch (error) {
        console.error('[TIME BUDGET] Error loading locations:', error);
        const locationList = document.getElementById('budget-location-filter-list');
        if (locationList) {
            locationList.innerHTML = '<div class="text-red-600 text-sm p-2">Error loading locations</div>';
        }
    } finally {
        budgetLocationsState.isLoading = false;
    }
}

function displayBudgetLocations() {
    const locationList = document.getElementById('budget-location-filter-list');
    if (!locationList) return;
    
    if (budgetLocationsState.allLocations.length === 0) {
        locationList.innerHTML = '<div class="px-3 py-2 text-sm text-gray-500">No locations found</div>';
        return;
    }
    
    let html = '';
    budgetLocationsState.allLocations.forEach(location => {
        const locationId = location._id || location.id;
        const isChecked = selectedBudgetLocations.has(locationId);
        
        html += `
            <div class="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer" onclick="selectBudgetLocation('${locationId}', '${location.name.replace(/'/g, "\\'")}')">
                <input type="checkbox" 
                       ${isChecked ? 'checked' : ''} 
                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                       style="pointer-events: none;">
                <label class="ml-2 text-sm text-gray-700 flex-1 cursor-pointer">
                    ${location.name}
                </label>
            </div>
        `;
    });
    
    if (budgetLocationsState.hasMore) {
        html += '<div class="px-3 py-2 text-sm text-gray-500 text-center">Scroll for more...</div>';
    }
    
    locationList.innerHTML = html;
    
    // Update checkbox state after rendering
    updateBudgetLocationCheckboxState();
}

function selectBudgetLocation(locationId, locationName) {
    const checkbox = event.target.closest('div').querySelector('input[type="checkbox"]');
    
    if (selectedBudgetLocations.has(locationId)) {
        selectedBudgetLocations.delete(locationId);
        checkbox.checked = false;
    } else {
        selectedBudgetLocations.add(locationId);
        checkbox.checked = true;
    }
    
    console.log('[TIME BUDGET] Selected locations:', Array.from(selectedBudgetLocations));
    updateBudgetLocationFilterDisplay();
    updateBudgetLocationCheckboxState();
    filterTimeBudget();
}

function updateBudgetLocationFilterDisplay() {
    const display = document.getElementById('budget-location-filter-display');
    if (!display) return;
    
    if (selectedBudgetLocations.size === 0) {
        display.textContent = t('time.allLocations');
    } else if (selectedBudgetLocations.size === 1) {
        // Find the location name
        const locationId = Array.from(selectedBudgetLocations)[0];
        const location = budgetLocationsState.allLocations.find(loc => (loc._id || loc.id) === locationId);
        display.textContent = location ? location.name : `1 ${t('common.location')}`;
    } else {
        display.textContent = `${selectedBudgetLocations.size} ${t('common.locations')}`;
    }
}

function handleBudgetLocationScroll(event) {
    const element = event.target;
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;
    
    // Load more when scrolled near bottom (within 50px)
    if (scrollHeight - scrollPosition < 50 && !budgetLocationsState.isLoading && budgetLocationsState.hasMore) {
        loadBudgetLocations(false);
    }
}

async function loadTimeBudgetData(forceRefresh = false) {
    const contentArea = document.getElementById('time-budget-content');
    if (!contentArea) return;

    // Reset the topbar stats row; it is repopulated only when a single location
    // is rendered in displayTimeBudgetResults.
    renderBudgetTopbarStats([], {});
    
    // Validate filters
    if (selectedBudgetPeriods.length === 0) {
        contentArea.innerHTML = `
            <div class="text-center py-12 text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900">Select Time Periods</h3>
                <p class="mt-1 text-sm text-gray-500">Please select date range to view time budget data</p>
            </div>
        `;
        return;
    }
    
    // Show loading state
    contentArea.innerHTML = `
        <div class="flex justify-center items-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
    `;
    
    try {
        // Get locations to query - only use explicitly selected locations
        let locationIds = [];
        if (selectedBudgetLocations.size > 0) {
            locationIds = Array.from(selectedBudgetLocations);
        }
        
        if (locationIds.length === 0) {
            contentArea.innerHTML = `
                <div class="text-center py-12 text-gray-500">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">No Locations</h3>
                    <p class="mt-1 text-sm text-gray-500">Please select accounts or locations to view time budget data</p>
                </div>
            `;
            return;
        }
        
        console.log('[TIME BUDGET] Loading data for periods:', selectedBudgetPeriods);
        console.log('[TIME BUDGET] Loading data for locations:', locationIds);
        
        // Fetch location details with budget info
        const locationsWithBudget = await fetchLocationsWithBudget(locationIds);
        
        // Fetch time tracking data for the selected periods and locations
        const timeTrackingData = await fetchTimeTrackingData(selectedBudgetPeriods, locationIds);
        
        // Ensure budget colouring settings are loaded before rendering
        await ensureTimeBudgetColorSettings(forceRefresh);

        // Calculate and display the results
        displayTimeBudgetResults(locationsWithBudget, timeTrackingData);
        
    } catch (error) {
        console.error('[TIME BUDGET] Error loading data:', error);
        contentArea.innerHTML = `
            <div class="text-center py-12 text-red-600">
                <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium">Error Loading Data</h3>
                <p class="mt-1 text-sm">${error.message || 'Failed to load time budget data'}</p>
            </div>
        `;
    }
}

async function fetchLocationsWithBudget(locationIds) {
    // Instead of fetching again, use the locations already loaded in the filter
    // Filter by the requested location IDs
    const locations = budgetLocationsState.allLocations.filter(loc => 
        locationIds.includes(loc._id || loc.id)
    );
    
    console.log('[TIME BUDGET] Using locations from filter:', locations.length);
    console.log('[TIME BUDGET] Location budget data:', locations.map(loc => ({
        name: loc.name,
        id: loc._id || loc.id,
        budget_hours: loc.time_budget_hours,
        budget_period: loc.time_budget_period
    })));
    
    return locations;
}

async function fetchTimeTrackingData(periods, locationIds) {
    // NEW OPTIMIZED VERSION: Uses server-side aggregation instead of fetching all events
    // This is 100x faster and doesn't overload the browser with large datasets
    
    // Build date ranges for each period
    const dateRanges = periods.map(period => {
        if (budgetPeriodType === 'weekly') {
            // Parse week format: "2025-W44"
            const [year, week] = period.key.split('-W').map(Number);
            return getWeekDateRange(year, week);
        } else if (budgetPeriodType === 'daily') {
            // Parse day format: "2025-11-03"
            return getDayDateRange(period.key);
        } else {
            // Parse month format: "2025-11"
            const [year, month] = period.key.split('-').map(Number);
            return getMonthDateRange(year, month);
        }
    });
    
    // Group events by location and period
    const groupedData = {};
    
    // Initialize structure for all locations and periods
    locationIds.forEach(locationId => {
        groupedData[locationId] = {};
        periods.forEach(period => {
            groupedData[locationId][period.key] = {
                hours: 0,
                eventCount: 0,
                events: [] // We don't fetch individual events anymore
            };
        });
    });
    
    // Fetch aggregated data for each period in parallel (server does the
    // heavy lifting). Was previously a sequential await loop, which made the
    // initial Time Budget render proportional to (#periods * round-trip).
    const summaryRequests = periods.map((period) => {
        const range = budgetPeriodType === 'weekly'
            ? getWeekDateRange(...period.key.split('-W').map(Number))
            : budgetPeriodType === 'daily'
                ? getDayDateRange(period.key)
                : getMonthDateRange(...period.key.split('-').map(Number));

        const params = new URLSearchParams();
        params.append('location_ids', locationIds.join(','));
        params.append('start_date', range.start);
        params.append('end_date', range.end);
        params.append('include_events', 'true');

        return apiCall(`/time-tracking/events/summary?${params.toString()}`, {
            method: 'GET',
            headers: auth.getAuthHeaders()
        }).then((response) => ({ period, response }))
          .catch((error) => {
              console.error(`[TIME BUDGET] Error fetching summary for period ${period.key}:`, error);
              return null;
          });
    });

    const summaryResults = await Promise.all(summaryRequests);

    for (const result of summaryResults) {
        if (!result) continue;
        const { period, response } = result;
        const summary = response.summary || {};
        locationIds.forEach((locationId) => {
            if (summary[locationId]) {
                groupedData[locationId][period.key] = {
                    hours: summary[locationId].total_hours || 0,
                    eventCount: summary[locationId].total_events || 0,
                    events: summary[locationId].events || []
                };
            }
        });
    }
    
    return groupedData;
}

function getWeekDateRange(year, week) {
    // Get Monday of the week
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dayOfWeek = simple.getDay();
    const monday = new Date(simple);
    monday.setDate(simple.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
    
    // Get Sunday (end of week)
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return {
        start: monday.toISOString(),
        end: sunday.toISOString()
    };
}

function getMonthDateRange(year, month) {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0, 23, 59, 59, 999);
    
    return {
        start: start.toISOString(),
        end: end.toISOString()
    };
}

function getDayDateRange(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const start = new Date(year, month - 1, day, 0, 0, 0, 0);
    const end = new Date(year, month - 1, day, 23, 59, 59, 999);
    return {
        start: start.toISOString(),
        end: end.toISOString()
    };
}

function formatBudgetMinutes(totalMinutes) {
    const rounded = Math.max(0, Math.round(totalMinutes));
    const hours = Math.floor(rounded / 60);
    const minutes = rounded % 60;
    return `${hours}h ${minutes}m`;
}

// Populate the single-location stats row inside the gray topbar. Shown only
// when exactly one location is selected; three compact figures for the whole
// selected range: days with at least one registration, total time, and average
// time per registered day.
function renderBudgetTopbarStats(locations, timeTrackingData) {
    const container = document.getElementById('budget-topbar-stats');
    if (!container) return;

    if (locations.length !== 1) {
        container.classList.add('hidden');
        container.innerHTML = '';
        return;
    }

    const location = locations[0];
    const locationId = location._id || location.id;

    let totalMinutes = 0;
    const daysWithActivity = new Set();

    selectedBudgetPeriods.forEach(period => {
        const periodData = timeTrackingData[locationId]?.[period.key];
        if (!periodData) return;
        totalMinutes += (periodData.hours || 0) * 60;
        (periodData.events || []).forEach(event => {
            const ref = event.check_in_time || event.check_out_time;
            if (!ref) return;
            const d = new Date(ref);
            if (isNaN(d.getTime())) return;
            daysWithActivity.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
        });
    });

    const dayCount = daysWithActivity.size;
    const avgMinutes = dayCount > 0 ? totalMinutes / dayCount : 0;

    const stat = (label, value, align) => `
            <div class="flex items-baseline gap-3 ${align}">
                <span class="text-sm text-gray-600">${label}:</span>
                <span class="text-sm font-semibold text-gray-900">${value}</span>
            </div>`;

    container.innerHTML = `
        <div class="grid grid-cols-3 gap-4 items-center">
            ${stat(t('time.budgetStatsDaysWithRegistration'), dayCount, 'justify-start')}
            ${stat(t('time.budgetStatsTotalTime'), formatBudgetMinutes(totalMinutes), 'justify-center')}
            ${stat(t('time.budgetStatsAvgPerDay'), formatBudgetMinutes(avgMinutes), 'justify-end')}
        </div>`;
    container.classList.remove('hidden');
}

function displayTimeBudgetResults(locations, timeTrackingData) {
    const contentArea = document.getElementById('time-budget-content');
    if (!contentArea) return;

    // Update the single-location stats row in the topbar (hidden unless exactly
    // one location is selected).
    renderBudgetTopbarStats(locations, timeTrackingData);
    
    console.log('[TIME BUDGET DISPLAY] Called with:', {
        locationCount: locations.length,
        timeTrackingData: timeTrackingData
    });
    
    // Store data for export
    budgetLocationsState.locationsData = {};
    budgetLocationsState.allEvents = [];
    
    locations.forEach(location => {
        const locationId = location._id || location.id;
        budgetLocationsState.locationsData[locationId] = {
            name: location.name,
            budgetHours: location.time_budget_hours,
            budgetPeriod: location.time_budget_period
        };
        
        // Collect all events for this location
        selectedBudgetPeriods.forEach(period => {
            const periodData = timeTrackingData[locationId]?.[period.key];
            if (periodData && periodData.events) {
                periodData.events.forEach(event => {
                    budgetLocationsState.allEvents.push({
                        ...event,
                        location_name: location.name,
                        location_id: locationId,
                        budget_hours: location.time_budget_hours
                    });
                });
            }
        });
    });

    // Make events available to time-tracking-modals.openEditTimeModal, which
    // looks up the event by id in timeTrackingState.allEvents. Without this,
    // clicking a check-in/out time in Time Budget silently does nothing.
    const existingIds = new Set(timeTrackingState.allEvents.map(e => e._id));
    budgetLocationsState.allEvents.forEach(ev => {
        if (!existingIds.has(ev._id)) {
            timeTrackingState.allEvents.push(ev);
            existingIds.add(ev._id);
        }
    });
    
    if (locations.length === 0) {
        contentArea.innerHTML = `
            <div class="text-center py-12 text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900">No Locations</h3>
                <p class="mt-1 text-sm text-gray-500">No locations found with the selected filters.</p>
            </div>
        `;
        return;
    }
    
    // Get all events for the selected periods to display in expanded view
    const allEvents = [];
    selectedBudgetPeriods.forEach(period => {
        locations.forEach(location => {
            const locationId = location._id || location.id;
            const periodData = timeTrackingData[locationId]?.[period.key];
            if (periodData && periodData.events) {
                allEvents.push(...periodData.events.map(e => ({...e, locationId, period: period.key, periodLabel: period.label})));
            }
        });
    });
    
    // Build table HTML with list-style header
    let html = `
        <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-200">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Location
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User Name
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Check In
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Check Out
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Hours
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Budget
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
    `;
    
    // Add rows for each location × period combination (one row per period)
    locations.forEach(location => {
        const locationId = location._id || location.id;
        const budgetHours = location.time_budget_hours;
        const budgetPeriod = location.time_budget_period;
        const hasBudget = budgetHours && budgetPeriod;

        selectedBudgetPeriods.forEach(period => {
            const periodData = timeTrackingData[locationId]?.[period.key];
            const totalMinutes = periodData ? (periodData.hours * 60) || 0 : 0;
            const totalEvents = periodData ? (periodData.eventCount || 0) : 0;

            // User preference: skip rows with no activity
            if (budgetHideEmptyPeriods && totalEvents === 0) {
                return;
            }

            // Convert total minutes to H.MM format for display
            const displayHours = Math.floor(totalMinutes / 60);
            const displayMinutes = Math.round(totalMinutes % 60);

            // Determine color based on budget vs actual (per period)
            let bgColor = '';
            let textColor = 'text-gray-700';

            if (hasBudget && totalMinutes > 0) {
                const periodBudgetMinutes = budgetHours * 60;
                const diffMinutes = totalMinutes - periodBudgetMinutes;
                const redLimitBelow = timeBudgetColorSettings.redLimitBelow;
                const redLimitAbove = timeBudgetColorSettings.redLimitAbove;

                // Default to green (close enough to budget); turn red only when
                // the deviation reaches a configured red limit on that side.
                let isRed = false;
                if (diffMinutes < 0 && redLimitBelow !== null) {
                    isRed = Math.abs(diffMinutes) >= redLimitBelow;
                } else if (diffMinutes > 0 && redLimitAbove !== null) {
                    isRed = diffMinutes >= redLimitAbove;
                }

                if (isRed) {
                    bgColor = 'bg-red-50';
                    textColor = 'text-red-700';
                } else {
                    bgColor = 'bg-green-50';
                    textColor = 'text-green-700';
                }
            } else if (!hasBudget && totalMinutes > 0) {
                bgColor = 'bg-blue-50';
                textColor = 'text-blue-700';
            }

            const rowKey = `${locationId}__${period.key}`;

            // Summary row (one per period)
            html += `
                    <tr class="hover:bg-gray-50 cursor-pointer ${bgColor}" onclick="toggleBudgetLocationExpand('${locationId}', '${period.key}')">
                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium ${textColor}">
                            <div class="flex items-center">
                                <svg id="expand-icon-${rowKey}" class="w-4 h-4 mr-2 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                                </svg>
                                <div>
                                    <div>${location.name}</div>
                                    <div class="text-xs font-normal text-gray-500">${period.label}</div>
                                </div>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <!-- Empty in summary view -->
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${totalEvents} ${t('time.events')}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <!-- Empty in summary view -->
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium ${textColor}">
                            ${displayHours}h ${displayMinutes}m
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${hasBudget ? (() => {
                                const periodBudgetMinutes = Math.round(budgetHours * 60);
                                const bH = Math.floor(periodBudgetMinutes / 60);
                                const bM = periodBudgetMinutes % 60;
                                return `${bH}h ${bM}m`;
                            })() : '<span class="text-gray-400 italic">No budget</span>'}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <!-- Empty in summary view -->
                        </td>
                    </tr>
        `;

            // Expandable detail rows (hidden by default) - one tbody per period row
            html += `<tbody id="budget-detail-${rowKey}" class="hidden bg-gray-50">`;

            const locationEvents = (periodData && periodData.events) ? [...periodData.events] : [];

            if (locationEvents.length > 0) {
                locationEvents.forEach(event => {
                const checkInTime = formatDateTime(event.check_in_time);
                const checkOutTime = formatDateTime(event.check_out_time);
                
                let totalTime = '-';
                if (event.duration_minutes) {
                    const hours = Math.floor(event.duration_minutes / 60);
                    const minutes = Math.round(event.duration_minutes % 60);
                    totalTime = `${hours}h ${minutes}m`;
                }
                
                // Determine color for username based on status
                const userNameColor = event.status === 'closed' ? 
                    'text-green-600 font-medium' : 
                    'text-yellow-600 font-medium';
                
                // Status indicator dots
                let statusDot = '';
                if (permissions.can('timeTracking', 'viewModifiedBadge') && event.is_modified) {
                    statusDot = `<span class="inline-block w-2 h-2 rounded-full bg-orange-500 mr-2" title="${t('time.modified')}"></span>`;
                } else if (permissions.can('timeTracking', 'viewManualBadge') && event.is_manual) {
                    statusDot = `<span class="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2" title="${t('time.manual')}"></span>`;
                }
                
                const canEdit = permissions.can('timeTracking', 'edit');
                const checkInClass = canEdit ? 'cursor-pointer hover:text-blue-600 hover:underline' : '';
                const checkOutClass = canEdit ? 'cursor-pointer hover:text-blue-600 hover:underline' : '';
                const editDataAttr = canEdit ? `data-event-id="${event._id}"` : '';
                
                html += `
                    <tr class="hover:bg-gray-100">
                        <td class="px-6 py-4 pl-12 whitespace-nowrap text-sm text-gray-400">
                            <!-- Location name grayed out in detail -->
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm ${userNameColor}">
                            <div class="flex items-center">
                                ${statusDot}
                                <span>${event.user_name || 'Unknown'}</span>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span class="${checkInClass}" ${editDataAttr} data-field="check_in_time" onclick="${canEdit ? 'openEditTimeModal(this)' : ''}">${checkInTime}</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span class="${checkOutClass}" ${editDataAttr} data-field="check_out_time" onclick="${canEdit ? 'openEditTimeModal(this)' : ''}">${checkOutTime}</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            ${totalTime}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <!-- Budget shown only in summary -->
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${canEdit ? `
                            <button onclick="event.stopPropagation(); deleteTimeTrackingEvent('${event._id}')" 
                                    class="text-red-600 hover:text-red-800">
                                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                            ` : ''}
                        </td>
                    </tr>
                `;
            });
        } else {
            html += `
                <tr>
                    <td colspan="7" class="px-6 py-4 text-center text-sm text-gray-400 italic">
                        No events for this period
                    </td>
                </tr>
            `;
        }
        
        html += `</tbody>`;
        }); // end selectedBudgetPeriods.forEach
    });
    
    html += `
                </tbody>
            </table>
        </div>
    `;
    
    contentArea.innerHTML = html;
}

async function toggleBudgetLocationExpand(locationId, periodKey) {
    console.log('[TIME BUDGET EXPAND] Called for location/period:', locationId, periodKey);
    const rowKey = periodKey ? `${locationId}__${periodKey}` : locationId;
    const detailElement = document.getElementById(`budget-detail-${rowKey}`);
    const iconElement = document.getElementById(`expand-icon-${rowKey}`);
    
    console.log('[TIME BUDGET EXPAND] detailElement:', detailElement);
    console.log('[TIME BUDGET EXPAND] iconElement:', iconElement);
    
    if (!detailElement || !iconElement) {
        console.log('[TIME BUDGET EXPAND] Missing elements, returning');
        return;
    }
    
    const isCurrentlyHidden = detailElement.classList.contains('hidden');
    console.log('[TIME BUDGET EXPAND] isCurrentlyHidden:', isCurrentlyHidden);
    console.log('[TIME BUDGET EXPAND] detailElement.classList:', detailElement.classList.toString());
    console.log('[TIME BUDGET EXPAND] detailElement.innerHTML.length:', detailElement.innerHTML.length);
    
    if (isCurrentlyHidden) {
        console.log('[TIME BUDGET EXPAND] Entering expand branch');
        // Expanding - fetch events if not already loaded
        // Check if we have real event data (not just "No events" placeholder)
        const hasPlaceholder = detailElement.innerHTML.includes('No events for this period');
        const hasNoContent = detailElement.innerHTML.trim() === '' || !detailElement.querySelector('tr');
        const needsData = hasNoContent || hasPlaceholder;
        
        console.log('[TIME BUDGET EXPAND] needsData:', needsData);
        console.log('[TIME BUDGET EXPAND] hasPlaceholder:', hasPlaceholder);
        
        if (needsData) {
            // Show loading state
            detailElement.classList.remove('hidden');
            detailElement.innerHTML = `
                <tr><td colspan="6" class="px-6 py-4 text-center text-gray-500">
                    <div class="flex items-center justify-center">
                        <svg class="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading events...
                    </div>
                </td></tr>
            `;
            
            try {
                // Fetch events for this location for the single period being expanded
                const periodsToFetch = periodKey
                    ? selectedBudgetPeriods.filter(p => p.key === periodKey)
                    : selectedBudgetPeriods;
                const events = await fetchEventsForLocation(locationId, periodsToFetch);
                
                // Render the events
                let html = '';
                if (events.length > 0) {
                    events.forEach(event => {
                        const checkInTime = formatDateTime(event.check_in_time);
                        const checkOutTime = formatDateTime(event.check_out_time);
                        
                        let totalTime = '-';
                        if (event.duration_minutes) {
                            const hours = Math.floor(event.duration_minutes / 60);
                            const minutes = Math.round(event.duration_minutes % 60);
                            totalTime = `${hours}h ${minutes}m`;
                        }
                        
                        const userNameColor = event.status === 'closed' ? 
                            'text-green-600 font-medium' : 
                            'text-yellow-600 font-medium';
                        
                        let statusDot = '';
                        if (permissions.can('timeTracking', 'viewModifiedBadge') && event.is_modified) {
                            statusDot = `<span class="inline-block w-2 h-2 rounded-full bg-orange-500 mr-2" title="${t('time.modified')}"></span>`;
                        } else if (permissions.can('timeTracking', 'viewManualBadge') && event.is_manual) {
                            statusDot = `<span class="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2" title="${t('time.manual')}"></span>`;
                        }
                        
                        const canEdit = permissions.can('timeTracking', 'edit');
                        const checkInClass = canEdit ? 'cursor-pointer hover:text-blue-600 hover:underline' : '';
                        const checkOutClass = canEdit ? 'cursor-pointer hover:text-blue-600 hover:underline' : '';
                        const editDataAttr = canEdit ? `data-event-id="${event._id}"` : '';
                        
                        html += `
                            <tr class="hover:bg-gray-100">
                                <td class="px-6 py-4 pl-12 whitespace-nowrap text-sm text-gray-400">
                                    <!-- Location column (empty in detail view) -->
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm ${userNameColor}">
                                    <div class="flex items-center">
                                        ${statusDot}
                                        <span>${event.user_name || 'Unknown'}</span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span class="${checkInClass}" ${editDataAttr} data-field="check_in_time" onclick="${canEdit ? 'openEditTimeModal(this)' : ''}">${checkInTime}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span class="${checkOutClass}" ${editDataAttr} data-field="check_out_time" onclick="${canEdit ? 'openEditTimeModal(this)' : ''}">${checkOutTime}</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    ${totalTime}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <!-- Budget column (empty in detail view) -->
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-right">
                                    ${canEdit ? `
                                        <button onclick="event.stopPropagation(); deleteTimeTrackingEvent('${event._id}')" 
                                                class="text-red-600 hover:text-red-900">
                                            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                            </svg>
                                        </button>
                                    ` : ''}
                                </td>
                            </tr>
                        `;
                    });
                } else {
                    html = `
                        <tr><td colspan="7" class="px-6 py-4 text-center text-gray-500">
                            No events found for this location
                        </td></tr>
                    `;
                }
                
                detailElement.innerHTML = html;
                iconElement.classList.add('rotate-90');
                
            } catch (error) {
                console.error('Error fetching events:', error);
                detailElement.innerHTML = `
                    <tr><td colspan="7" class="px-6 py-4 text-center text-red-500">
                        Error loading events: ${error.message}
                    </td></tr>
                `;
            }
        } else {
            // Data already loaded, just show it
            detailElement.classList.remove('hidden');
            iconElement.classList.add('rotate-90');
        }
    } else {
        // Collapsing
        console.log('[TIME BUDGET EXPAND] Entering collapse branch');
        detailElement.classList.add('hidden');
        iconElement.classList.remove('rotate-90');
    }
}

// Helper function to fetch events for a specific location
async function fetchEventsForLocation(locationId, periods) {
    // Fetch all periods in parallel instead of sequentially. The Time Budget
    // expansion previously did one request per period awaited in series, so
    // e.g. 7 daily periods x ~400ms latency = ~2.8s. Promise.all collapses
    // this into roughly one round-trip.
    const requests = periods.map((period) => {
        const range = budgetPeriodType === 'weekly'
            ? getWeekDateRange(...period.key.split('-W').map(Number))
            : budgetPeriodType === 'daily'
                ? getDayDateRange(period.key)
                : getMonthDateRange(...period.key.split('-').map(Number));

        const params = new URLSearchParams();
        params.append('location_id', locationId);
        params.append('start_date', range.start);
        params.append('end_date', range.end);
        params.append('status', 'closed');

        return apiCall(`/time-tracking/events?${params.toString()}`, {
            method: 'GET',
            headers: auth.getAuthHeaders()
        }).catch((error) => {
            console.error(`Error fetching events for period ${period.key}:`, error);
            return null;
        });
    });

    const responses = await Promise.all(requests);

    const allEvents = [];
    for (const response of responses) {
        if (response && Array.isArray(response.events)) {
            allEvents.push(...response.events);
        }
    }

    // Sort by check-in time, newest first
    allEvents.sort((a, b) => new Date(b.check_in_time) - new Date(a.check_in_time));

    return allEvents;
}

function exportTimeBudget() {
    // Base columns that everyone can see - same as Time Tracking
    const baseColumns = [
        { id: 'location_name', label: 'Location', checked: true },
        { id: 'user_name', label: 'User Name', checked: true },
        { id: 'check_in_time', label: 'Check In Time', checked: true },
        { id: 'check_out_time', label: 'Check Out Time', checked: true },
        { id: 'duration', label: 'Total Time (Hours:Minutes:Seconds)', checked: true },
        { id: 'accumulated_hours', label: 'Accumulated Hours', checked: true }, // New column for accumulated total
        { id: 'budget_hours', label: 'Budget Hours', checked: true }, // New column for budget
        { id: 'status', label: 'Status', checked: false }
    ];
    
    // Additional columns only visible to Administrator and Team leader
    const adminColumns = [
        { id: 'account_name', label: 'Account', checked: false },
        { id: 'is_modified', label: 'Modified', checked: false },
        { id: 'is_manual', label: 'Manual Entry', checked: false },
        { id: 'created_at', label: 'Created Date', checked: false },
        { id: 'modified_by', label: 'Last Modified By', checked: false },
        { id: 'modified_at', label: 'Last Modified Date', checked: false }
    ];
    
    // Determine which columns to show based on permissions
    let columns = [...baseColumns];
    
    if (permissions.can('timeTracking', 'viewModifiedBadge')) {
        columns = [...columns, ...adminColumns];
    }
    
    showExportModal('Time Budget', columns, exportTimeBudgetToExcel);
}

function exportTimeBudgetToExcel() {
    const selectedColumns = Array.from(document.querySelectorAll('.export-column-checkbox:checked')).map(cb => cb.value);
    
    if (selectedColumns.length === 0) {
        showError('Please select at least one column to export');
        return;
    }
    
    if (budgetLocationsState.allEvents.length === 0) {
        showError('No events to export. Please load time budget data first.');
        return;
    }
    
    // Group events by location to identify last event per location
    const eventsByLocation = {};
    budgetLocationsState.allEvents.forEach(event => {
        const locId = event.location_id;
        if (!eventsByLocation[locId]) {
            eventsByLocation[locId] = [];
        }
        eventsByLocation[locId].push(event);
    });
    
    // Create export data
    const data = [];
    Object.keys(eventsByLocation).forEach(locationId => {
        const locationEvents = eventsByLocation[locationId];
        
        // Calculate total accumulated hours for this location
        const accumulatedMinutes = locationEvents.reduce((sum, event) => {
            return sum + (event.duration_minutes || 0);
        }, 0);
        const accumulatedHours = accumulatedMinutes / 60;
        
        locationEvents.forEach((event, index) => {
            const row = {};
            const isLastForLocation = index === locationEvents.length - 1;
            
            selectedColumns.forEach(col => {
                switch (col) {
                    case 'location_name':
                        row['Location'] = event.location_name || 'Unknown';
                        break;
                    case 'user_name':
                        row['User Name'] = event.user_name || 'Unknown';
                        break;
                    case 'check_in_time':
                        row['Check In Time'] = formatDateTime(event.check_in_time);
                        break;
                    case 'check_out_time':
                        row['Check Out Time'] = formatDateTime(event.check_out_time);
                        break;
                    case 'duration':
                        if (event.duration_minutes) {
                            // Store as Excel time value (fraction of a day: minutes / 1440)
                            // Excel will format this with [h]:mm to show hours:minutes
                            row['Total Time (Hours)'] = event.duration_minutes / 1440;
                        } else {
                            row['Total Time (Hours)'] = 0;
                        }
                        break;
                    case 'accumulated_hours':
                        // Only show accumulated total on last row for each location, and only if duration column is selected
                        if (isLastForLocation && selectedColumns.includes('duration')) {
                            // Store as Excel time value (fraction of a day)
                            row['Accumulated Hours'] = accumulatedMinutes / 1440;
                        } else {
                            row['Accumulated Hours'] = '';
                        }
                        break;
                    case 'budget_hours':
                        // Only show budget on last row for each location
                        row['Budget Hours'] = isLastForLocation && event.budget_hours ? event.budget_hours : '';
                        break;
                    case 'status':
                        row['Status'] = event.status === 'closed' ? 'Completed' : 'Open';
                        break;
                    case 'account_name':
                        row['Account'] = event.account_name || 'Unknown';
                        break;
                    case 'is_modified':
                        row['Modified'] = event.is_modified ? 'Yes' : 'No';
                        break;
                    case 'is_manual':
                        row['Manual Entry'] = event.is_manual ? 'Yes' : 'No';
                        break;
                    case 'created_at':
                        row['Created Date'] = formatDateTime(event.created_at);
                        break;
                    case 'modified_by':
                        row['Last Modified By'] = event.modified_by_name || '-';
                        break;
                    case 'modified_at':
                        row['Last Modified Date'] = formatDateTime(event.modified_at);
                        break;
                }
            });
            data.push(row);
        });
    });
    
    // Generate filename with first location and timestamp (European format: YYYY-DD-MM)
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
    const firstLocation = data.length > 0 ? (data[0]['Location'] || 'Unknown').replace(/[^a-zA-Z0-9]/g, '_') : 'Unknown';
    const filename = `time_budget_${firstLocation}_${timestamp}.xlsx`;
    
    exportToExcel(data, filename);
    closeModal();
    showSuccess(`Exported ${data.length} time budget events successfully`);
}


// ============================================


// ============================================================================
// EXPORTS
// ============================================================================

// Export main function
export { showTimeBudgetTab };

// Window exports for onclick handlers
window.showTimeBudgetTab = showTimeBudgetTab;
window.toggleBudgetPeriodType = toggleBudgetPeriodType;
window.toggleBudgetPeriodRangePicker = toggleBudgetPeriodRangePicker;
window.updateBudgetPeriodRange = updateBudgetPeriodRange;
window.goToBudgetToday = goToBudgetToday;
window.toggleBudgetHideEmptyPeriods = toggleBudgetHideEmptyPeriods;
window.toggleBudgetAccountFilterExpanded = toggleBudgetAccountFilterExpanded;
window.toggleBudgetLocationFilterExpanded = toggleBudgetLocationFilterExpanded;
window.clearBudgetAccountFilter = clearBudgetAccountFilter;
window.clearBudgetLocationFilter = clearBudgetLocationFilter;
window.toggleAllBudgetLocations = toggleAllBudgetLocations;
window.handleBudgetLocationScroll = handleBudgetLocationScroll;
window.toggleBudgetAccountItem = toggleBudgetAccountItem;
window.selectBudgetAccount = selectBudgetAccount;
window.selectBudgetLocation = selectBudgetLocation;
window.clearTimeBudgetFilters = clearTimeBudgetFilters;
window.loadTimeBudgetData = loadTimeBudgetData;
window.toggleBudgetLocationExpand = toggleBudgetLocationExpand;
window.exportTimeBudget = exportTimeBudget;
window.closeModal = closeModal;
